// Dependencies
import { z } from "zod";

import { GetInAppPromotionResponse } from "@performyard/service-contracts";
import { SIDE_NAV_VOCABULARY, coercedBoolean } from "@performyard/utils-isomorphic";

// local imports
import request from "../base-request";

const sideNavThemeSchema = z.object({
	id: z.string(),
	name: z.string(),
	backgroundColor: z.string(),
	foregroundColor: z.string(),
	isDefault: coercedBoolean,
});

export const SIDE_NAV_AREAS = SIDE_NAV_VOCABULARY.areas;
export const SIDE_NAV_TAGS = SIDE_NAV_VOCABULARY.tags;

export type SideNavArea = (typeof SIDE_NAV_AREAS)[number];
export type SideNavTag = (typeof SIDE_NAV_TAGS)[number];
export type VisibleNavTags = Partial<Record<SideNavArea, SideNavTag[]>> & Record<string, string[]>;

const visibleNavTagsSchema = z.record(z.string(), z.array(z.string())).transform((tags) => tags as VisibleNavTags);

/**
 * The company's active in-app promotion, as delivered on the nav payload. Only the fields the
 * navs consume are listed: `build_promotion_nav_payload` in
 * webserver/service_actions/navigation_config.py deliberately narrows the raw promotion document
 * to these, keeping operator-only data (audit_log, audience configuration) off a response every
 * authenticated user receives.
 *
 * `type` and `companiesWithModule` both `.catch()` rather than throwing: a malformed promotion
 * document must not take down the entire navigation, and falling back to undefined/null lands on
 * the default (does-not-own-the-module) audience, which is exactly how the Python twin treats a
 * non-boolean value.
 */
const currentPromotionSchema = z.object({
	_id: z.string().optional(),
	type: z.string().optional().catch(undefined),
	hyperlink: z.string().optional(),
	developmentOptions: GetInAppPromotionResponse.shape.developmentOptions,
	companiesWithModule: z.boolean().nullish().catch(null),
});

export const navigationConfigSchema = z.object({
	currentUserId: z.string(),
	currentUserName: z.string(),
	currentUserJobTitle: z.string(),
	currentUserEmail: z.string().optional().default(""),
	companyId: z.string(),
	theUserId: z.string(),
	isViewingSelf: coercedBoolean,
	currentNav: z.string().nullable(),
	isAdmin: coercedBoolean,
	isBasicAdmin: coercedBoolean,
	isBillingAdmin: coercedBoolean.optional().default(false),
	isEngagementOnly: coercedBoolean,
	isMeetingsEnabled: coercedBoolean.optional().default(false),
	isEngagementEnabled: coercedBoolean.optional().default(false),
	isSurveysEnabled: coercedBoolean.optional().default(false),
	isCompensationEnabled: coercedBoolean.optional().default(false),
	isCompetenciesEnabled: coercedBoolean.optional().default(false),
	isOverlord: coercedBoolean,
	hasDirectReports: coercedBoolean.optional().default(false),
	possessor: z
		.object({
			name: z.string(),
			id: z.string(),
		})
		.nullable(),
	visibleNavTags: visibleNavTagsSchema.optional(),
	navColor: z.string().optional().default(""),
	iconColor: z.string().optional().default(""),
	preferredNavigation: z.enum(["classic", "new"]).optional().default("classic"),
	sideNavTheme: sideNavThemeSchema.optional(),
	currentPromotion: currentPromotionSchema.optional().default({}),
	logoUrl: z.string().optional().default(""),
	mobileLogoUrl: z.string().optional().default(""),
});

export const navigationConfigResponseSchema = z.object({
	navigationConfig: navigationConfigSchema,
});

export type NavigationConfig = z.infer<typeof navigationConfigSchema>;
export type NavigationConfigResponse = z.infer<typeof navigationConfigResponseSchema>;
export type CurrentPromotion = z.infer<typeof currentPromotionSchema>;

/**
 * Whether a promotion's chosen audience includes a company with this module state.
 *
 * Promotions target companies WITHOUT the module by default; `companiesWithModule: true` flips the
 * audience to companies that already have it. This is the single source for the audience rule — the
 * dashboard banner (via its negation) and the nav ads' default-audience branch both derive from it,
 * and `should_show_module_promotion_ad` in webserver/service_actions/navigation_config.py is the
 * Python twin. The `=== true` check is strict on purpose: anything else (null, absent, or a
 * malformed non-boolean) means the default audience, matching the Python twin's `is True`.
 *
 * Note the nav ads (`shouldShowPromotionalAd`) go further and never render for a
 * `companiesWithModule: true` promotion at all — that audience gets the dashboard banner only,
 * since a nav "NEW" ad would duplicate the module's own real nav item.
 */
export const promotionAudienceMatchesModule = (
	companiesWithModule: boolean | null | undefined,
	moduleEnabled: boolean
): boolean => (companiesWithModule === true ? moduleEnabled : !moduleEnabled);

/**
 * Whether the server exposed a nav area (and optionally a tag within it) to this user. Omit `tag`
 * to ask only whether the area is present. Missing area means not visible, so gates fail closed.
 */
export const hasVisibleTag = (
	config: Pick<NavigationConfig, "visibleNavTags">,
	area: SideNavArea,
	tag?: SideNavTag
): boolean => {
	const tagsForArea = config.visibleNavTags?.[area];
	if (tagsForArea === undefined) return false;
	if (tag === undefined) return true;
	return tagsForArea.includes(tag);
};

/**
 * Get navigation config for current viewing context.
 * Optionally pass employeeId to get config for viewing a specific employee.
 */
export const getNavigationConfig = async (employeeId?: string) => {
	const url = employeeId ? `/api/navigation-config?employeeId=${employeeId}` : "/api/navigation-config";
	const [data] = await request(url);
	return navigationConfigResponseSchema.parse(data);
};
